import { Entity, stringifyEntityRef } from "@backstage/catalog-model";
import { discoveryApiRef, fetchApiRef, microsoftAuthApiRef, useApi } from "@backstage/core-plugin-api";
import { catalogApiRef } from "@backstage/plugin-catalog-react";
import { Chip, CircularProgress, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl, InputLabel, MenuItem, Select, TextField } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import React, { useEffect, useMemo, useState } from "react";
import { useAsync, useDebounce } from "react-use";
import { WorkspacesService } from "../../services/WorkspacesService";
import { CatalogData, Environment, SimpleWorkspace } from "../../pages/workspaces/types";
import { useNotification } from "../../hooks/useNotification";
import { NotificationSnackbar } from "../notification-snackbar/notification-snackbar";

const MAX_WORKSPACES = 15;

interface UpdateInsightsTablesDialogProps {
  open: boolean;
  systemEntity?: Entity;
  onClose: (message: string) => void;
}

export const UpdateInsightsTablesDialog = ({ open, systemEntity, onClose }: UpdateInsightsTablesDialogProps) => {
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const microsoftAuthApi = useApi(microsoftAuthApiRef);
  const catalogApi = useApi(catalogApiRef);

  const workspacesService = useMemo(
    () => new WorkspacesService(fetchApi, microsoftAuthApi, discoveryApi, catalogApi),
    [fetchApi, microsoftAuthApi, discoveryApi, catalogApi],
  );

  const { notification, showError, hideNotification } = useNotification();
  const [environment, setEnvironment] = useState<Environment | null>(null);
  const [selectedWorkspaces, setSelectedWorkspaces] = useState<SimpleWorkspace[]>([]);
  const [racIdSearch, setRacIdSearch] = useState('');
  const [debouncedRacId, setDebouncedRacId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const systemId = systemEntity?.metadata.annotations?.['open.totvs.io/app-code'] || '';

  useDebounce(() => { setDebouncedRacId(racIdSearch); }, 400, [racIdSearch]);

  const { loading: loadingWorkspaces, value: workspaceOptions = [] } = useAsync(async () => {
    if (!environment || !systemId) return [];
    const result = await workspacesService.getWorkspaces(environment, {
      systemId,
      addonId: 'add001',
      partialRacId: debouncedRacId || undefined,
    });
    return result.items;
  }, [environment, debouncedRacId, workspacesService, systemId]);

  useEffect(() => {
    if (!open) {
      setEnvironment(null);
      setSelectedWorkspaces([]);
      setRacIdSearch('');
      setDebouncedRacId('');
      setIsSubmitting(false);
    }
  }, [open]);

  const mergedOptions = useMemo(() => {
    const optionIds = new Set(workspaceOptions.map(w => w.workspaceId));
    const extraSelected = selectedWorkspaces.filter(w => !optionIds.has(w.workspaceId));
    return [...workspaceOptions, ...extraSelected];
  }, [workspaceOptions, selectedWorkspaces]);

  const handleConfirm = async (): Promise<void> => {
    if (systemEntity && environment) {
      setIsSubmitting(true);
      try {
        const components = await workspacesService.getInsightComponents(environment, systemEntity);
        const catalogData: CatalogData = {
          system: {
            id: systemEntity.metadata.annotations?.['open.totvs.io/app-code'] || '',
            ref: stringifyEntityRef(systemEntity),
            name: systemEntity.metadata.title || systemEntity.metadata.name,
          },
          addon: {
            id: 'add001',
            ref: 'system:supply-engenharia/insights',
            name: 'Insights',
            insightsProductFolderName: systemEntity.metadata.annotations?.['open.totvs.io/insights-folder-name'],
            components: components,
          },
        };

        const command = {
          catalogData,
          workspaces: selectedWorkspaces.map(w => w.workspaceId),
        };

        await workspacesService.reprocessTables(environment, command);
        onClose('Tables update initiated successfully');
      } catch (error) {
        showError('Failed to initiate tables update. Please, try again later.');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{`Update the Insights tables for ${systemEntity?.metadata?.title} across all workspaces?`}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          This action will update the system databases used by Insights and Data Sharing across all workspaces, including any newly added tables.
          <p><strong>This operation is resource-intensive and may take several minutes to complete.</strong></p>
          Would you like to proceed?
        </DialogContentText>
        <FormControl style={{ width: '50%' }} margin="normal">
          <InputLabel id="environment-select-label">Environment</InputLabel>
          <Select
            labelId="environment-select-label"
            value={environment ?? ''}
            onChange={e => {
              setEnvironment((e.target.value as Environment) || null);
              setSelectedWorkspaces([]);
              setRacIdSearch('');
              setDebouncedRacId('');
            }}
          >
            <MenuItem value="staging">Staging</MenuItem>
            <MenuItem value="production">Production</MenuItem>
          </Select>
        </FormControl>
        <FormControl fullWidth margin="normal">
          <Autocomplete
            multiple
            disabled={!environment}
            options={mergedOptions}
            value={selectedWorkspaces}
            inputValue={racIdSearch}
            onInputChange={(_, value, reason) => {
              if (reason !== 'reset') setRacIdSearch(value);
            }}
            getOptionLabel={option => `${option.racId} - ${option.name}`}
            getOptionSelected={(option, value) => option.workspaceId === value.workspaceId}
            onChange={(_, newValue) => {
              if (newValue.length <= MAX_WORKSPACES) {
                setSelectedWorkspaces(newValue);
              }
            }}
            loading={loadingWorkspaces}
            noOptionsText={environment ? 'No workspaces found' : 'Select an environment first'}
            renderInput={params => (
              <TextField
                {...params}
                label="Workspaces"
                variant="standard"
                placeholder={selectedWorkspaces.length === 0 ? 'Search by RAC ID...' : ''}
                helperText={
                  selectedWorkspaces.length >= MAX_WORKSPACES
                    ? `Maximum of ${MAX_WORKSPACES} workspaces reached`
                    : `${selectedWorkspaces.length}/${MAX_WORKSPACES} selected`
                }
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingWorkspaces && <CircularProgress size={16} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  key={option.workspaceId}
                  label={option.racId}
                  size="small"
                  {...getTagProps({ index })}
                />
              ))
            }
          />
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose('')} color="primary" disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          color="secondary"
          disabled={!systemEntity || !environment || selectedWorkspaces.length === 0 || isSubmitting}
          onClick={() => handleConfirm()}
        >
          {isSubmitting && <CircularProgress size={16} style={{ marginRight: 8 }} />}
          Confirm
        </Button>
      </DialogActions>

      <NotificationSnackbar notification={notification} onClose={hideNotification} />
    </Dialog>
  );
};